import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Bell, Check, CheckCheck, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { getAlerts, markAlertRead } from '../lib/api';
import { cn } from '../lib/utils';

export const Alerts = () => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAlerts();
    }, []);

    const loadAlerts = async () => {
        try {
            setLoading(true);
            const response = await getAlerts();
            setAlerts(response.data);
        } catch (error) {
            console.error('Failed to load alerts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkRead = async (alertId) => {
        try {
            await markAlertRead(alertId);
            setAlerts(prev => prev.map(a => 
                a.id === alertId ? { ...a, read: 1 } : a
            ));
            toast.success('Alert marked as read');
        } catch (error) {
            console.error('Failed to mark alert as read:', error);
            toast.error('Failed to update alert');
        }
    };

    const handleMarkAllRead = async () => {
        try {
            const unreadAlerts = alerts.filter(a => !a.read);
            await Promise.all(unreadAlerts.map(a => markAlertRead(a.id)));
            setAlerts(prev => prev.map(a => ({ ...a, read: 1 })));
            toast.success('All alerts marked as read');
        } catch (error) {
            console.error('Failed to mark all alerts as read:', error);
            toast.error('Failed to update alerts');
        }
    };

    const unreadCount = alerts.filter(a => !a.read).length;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="alerts-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-card-foreground flex items-center gap-3">
                        <Bell className="h-8 w-8 text-primary" />
                        Alerts
                        {unreadCount > 0 && (
                            <Badge variant="destructive">{unreadCount} unread</Badge>
                        )}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Stay informed about anomaly detections
                    </p>
                </div>

                {unreadCount > 0 && (
                    <Button 
                        variant="outline"
                        onClick={handleMarkAllRead}
                        data-testid="mark-all-read-btn"
                    >
                        <CheckCheck className="h-4 w-4 mr-2" />
                        Mark All Read
                    </Button>
                )}
            </div>

            {/* Alerts List */}
            {loading ? (
                <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-20" />
                    ))}
                </div>
            ) : alerts.length === 0 ? (
                <Card className="bg-card border-border">
                    <CardContent className="py-12 text-center">
                        <Bell className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                        <p className="text-lg text-card-foreground mb-2">No alerts yet</p>
                        <p className="text-muted-foreground">
                            Alerts will appear here when anomalies are detected
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {alerts.map((alert) => (
                        <Card 
                            key={alert.id}
                            className={cn(
                                "bg-card border-border transition-all duration-200",
                                !alert.read && "border-l-4 border-l-primary"
                            )}
                            data-testid={`alert-${alert.id}`}
                        >
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3">
                                        <div className={cn(
                                            "p-2 rounded-lg mt-1",
                                            alert.read ? "bg-muted" : "bg-primary/10"
                                        )}>
                                            <AlertTriangle className={cn(
                                                "h-4 w-4",
                                                alert.read ? "text-muted-foreground" : "text-primary"
                                            )} />
                                        </div>
                                        <div>
                                            <p className={cn(
                                                "text-sm",
                                                alert.read ? "text-muted-foreground" : "text-card-foreground"
                                            )}>
                                                {alert.message}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {format(new Date(alert.created_at), 'MMM d, yyyy h:mm a')}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {!alert.read && (
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => handleMarkRead(alert.id)}
                                            data-testid={`mark-read-${alert.id}`}
                                        >
                                            <Check className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Alerts;
